/* eslint-disable max-lines-per-function */
import { inject, Injectable } from '@angular/core';
import { ValidationErrors, ValidatorFn } from '@angular/forms';

import { Store } from '@ngrx/store';

import { NEGATIVE_POSITIVE_NUMBER_WITH_SEPARATORS_PATTERN } from '@core/common-legacy';
import { DevFlagSelectors } from '@core/dev-flags-data';
import { DevFlag } from '@core/dev-flags-types';
import { AutomationConditionType } from '@core/workflow-automations-types';
import {
  AutomationConditionErrorsType,
  AutomationConditionForm,
} from '@recruitee/workflow-automations-types';

const INTEGER_PATTERN = new RegExp('^(-*([0-9]+))$');

@Injectable()
export class AutomationConditionsValidationService {
  private readonly store = inject(Store);

  private hasWorkflowAutomationImprovementsFlag = this.store.selectSignal(
    DevFlagSelectors.isEnabled(DevFlag.WorkflowAutomationImprovements),
  );

  public conditionValidator(): ValidatorFn {
    return (control: AutomationConditionForm): ValidationErrors | null => {
      const conditionFormValue = control.getRawValue();

      switch (conditionFormValue.type) {
        case AutomationConditionType.CandidateEventOrganizer:
          const organizer = conditionFormValue.data?.admin_id;
          if (!organizer) {
            return { [AutomationConditionErrorsType.EmptyOrganizer]: true };
          }
          break;
        case AutomationConditionType.CandidatePreferredLocation:
          const preferredLocation = conditionFormValue.data?.location_id;
          if (!preferredLocation) {
            return { [AutomationConditionErrorsType.EmptyPreferredLocation]: true };
          }
          break;
        case AutomationConditionType.CandidateTags:
          const tags = <string[]>conditionFormValue.data?.tags;
          if (!tags?.length) {
            return { [AutomationConditionErrorsType.EmptyTags]: true };
          }
          break;
        case AutomationConditionType.CandidateEventInterviewersInclude:
          const interviewers = <string[]>conditionFormValue.data?.interviewer_ids;
          if (!interviewers?.length) {
            return { [AutomationConditionErrorsType.EmptyInterviewers]: true };
          }
          break;
        case AutomationConditionType.CandidateTaskAssigneesInclude:
          const assignees = <string[]>conditionFormValue.data?.admin_ids;
          if (!assignees?.length) {
            return { [AutomationConditionErrorsType.EmptyAssignees]: true };
          }
          break;
        case AutomationConditionType.CandidateSourceTags:
          const sourceTag = conditionFormValue.data?.source_tag;
          if (!sourceTag) {
            return { [AutomationConditionErrorsType.EmptySourceTags]: true };
          }
          break;
        case AutomationConditionType.CandidateOrigin:
          const source = conditionFormValue.data?.source;
          if (!source) {
            return { [AutomationConditionErrorsType.EmptyOrigin]: true };
          }
          break;
        case AutomationConditionType.CandidateMovedFromStage:
          const fromStage = conditionFormValue.data?.stage_id;
          if (!fromStage) {
            return { [AutomationConditionErrorsType.EmptyFromStage]: true };
          }
          break;
        case AutomationConditionType.CandidateStartDate:
        case AutomationConditionType.CandidateTaskDueDate:
        case AutomationConditionType.CandidateEventDate:
          const timeframe = conditionFormValue.data?.timeframe;
          if (!timeframe) {
            return { [AutomationConditionErrorsType.EmptyTimeFrame]: true };
          }

          const within = conditionFormValue.data?.within;
          const period = conditionFormValue.data?.period;

          if (timeframe === 'within_next' || timeframe === 'within_previous') {
            let hasWithinError = false;
            const isWithinEmpty =
              within === null || within === undefined || (<string>within).toString().trim() === '';
            hasWithinError = isWithinEmpty;
            const isWithinNotNumber =
              !hasWithinError &&
              !NEGATIVE_POSITIVE_NUMBER_WITH_SEPARATORS_PATTERN.test(
                this.formatNumberWithSeparators(within?.toString() || ''),
              );
            hasWithinError = hasWithinError ? hasWithinError : isWithinNotNumber;
            const isWithinNotInteger =
              !hasWithinError &&
              !(Number.isInteger(Number(within)) && INTEGER_PATTERN.test(<string>within));
            hasWithinError = hasWithinError ? hasWithinError : isWithinNotInteger;
            const isWithinNotGraterThanZero = !hasWithinError && <number>within <= 0;
            hasWithinError = hasWithinError ? hasWithinError : isWithinNotGraterThanZero;
            const isPeriodEmpty = !period;

            const withinControl = control.controls.data.controls.within;
            if (hasWithinError) {
              withinControl.setErrors({
                ...(isWithinEmpty ? { [AutomationConditionErrorsType.EmptyWithin]: true } : {}),
                ...(isWithinNotNumber
                  ? { [AutomationConditionErrorsType.NotNumberWithin]: true }
                  : {}),
                ...(isWithinNotInteger
                  ? { [AutomationConditionErrorsType.NotIntegerWithin]: true }
                  : {}),
                ...(isWithinNotGraterThanZero
                  ? { [AutomationConditionErrorsType.NotGraterThanZeroWithin]: true }
                  : {}),
              });
            } else {
              withinControl.setErrors(null);
            }

            if (hasWithinError || isPeriodEmpty) {
              return {
                ...(isWithinEmpty ? { [AutomationConditionErrorsType.EmptyWithin]: true } : {}),
                ...(isWithinNotNumber
                  ? { [AutomationConditionErrorsType.NotNumberWithin]: true }
                  : {}),
                ...(isWithinNotInteger
                  ? { [AutomationConditionErrorsType.NotIntegerWithin]: true }
                  : {}),
                ...(isWithinNotGraterThanZero
                  ? { [AutomationConditionErrorsType.NotGraterThanZeroWithin]: true }
                  : {}),
                ...(isPeriodEmpty ? { [AutomationConditionErrorsType.EmptyPeriod]: true } : {}),
              };
            }
          }

          const dateFrom = conditionFormValue.data?.date_from;
          const dateTo = conditionFormValue.data?.date_to;

          if (timeframe === 'date_range') {
            const isDateFromEmpty = !dateFrom;
            const isDateToEmpty = !dateTo;
            return isDateFromEmpty || isDateToEmpty
              ? {
                  ...(isDateFromEmpty
                    ? { [AutomationConditionErrorsType.EmptyDateFrom]: true }
                    : {}),
                  ...(isDateToEmpty ? { [AutomationConditionErrorsType.EmptyDateTo]: true } : {}),
                }
              : null;
          }

          const date = conditionFormValue.data?.date;

          if (timeframe === 'exact_date') {
            const isDateEmpty = !date;
            return isDateEmpty ? { [AutomationConditionErrorsType.EmptyDate]: true } : null;
          }

          break;
        case AutomationConditionType.CandidateEvaluationScore:
          const rating = conditionFormValue.data?.rating;
          const ratings = <string[]>conditionFormValue.data?.ratings;
          if (this.hasWorkflowAutomationImprovementsFlag() ? !ratings?.length : !rating) {
            return { [AutomationConditionErrorsType.EmptyRating]: true };
          }
          break;
        case AutomationConditionType.CandidateStage:
          const stage = conditionFormValue.data?.key;
          if (!stage) {
            return { [AutomationConditionErrorsType.EmptyStage]: true };
          }
          break;
        case AutomationConditionType.CandidateAssignedToJobTalentPool:
          const offers = <string[]>conditionFormValue.data?.offer_ids || [];
          const talentPools = <string[]>conditionFormValue.data?.talent_pool_ids || [];
          if (!offers.length && !talentPools.length) {
            return { [AutomationConditionErrorsType.EmptyAssignedTo]: true };
          }
          break;
        case AutomationConditionType.JobLocation:
          const locationIds = <string[]>conditionFormValue.data?.location_ids;
          if (!locationIds?.length) {
            return { [AutomationConditionErrorsType.EmptyLocation]: true };
          }
          break;
        case AutomationConditionType.JobDepartment:
          const department = conditionFormValue.data?.department_id;
          if (!department) {
            return { [AutomationConditionErrorsType.EmptyDepartment]: true };
          }
          break;
        case AutomationConditionType.JobRecruiter:
          const recruiter = conditionFormValue.data?.admin_id;
          if (!recruiter) {
            return { [AutomationConditionErrorsType.EmptyRecruiter]: true };
          }
          break;
        case AutomationConditionType.JobHiringManager:
          const hiringManager = conditionFormValue.data?.admin_id;
          if (!hiringManager) {
            return { [AutomationConditionErrorsType.EmptyHiringManager]: true };
          }
          break;
        case AutomationConditionType.JobWorkModel:
          const workModels = <string[]>conditionFormValue.data?.work_models;
          if (!workModels?.length) {
            return { [AutomationConditionErrorsType.EmptyWorkModel]: true };
          }
          break;
        case AutomationConditionType.JobCreator:
          const creator = conditionFormValue.data?.admin_id;
          if (!creator) {
            return { [AutomationConditionErrorsType.EmptyCreator]: true };
          }
          break;
        case AutomationConditionType.JobPreviousStatus:
          const previousStatus = conditionFormValue.data?.status;
          if (!previousStatus) {
            return { [AutomationConditionErrorsType.EmptyPreviousStatus]: true };
          }
          break;
        default:
          const conditionType = conditionFormValue.type;
          if (!conditionType) {
            return { [AutomationConditionErrorsType.EmptyCondition]: true };
          }
          break;
      }

      return null;
    };
  }

  private formatNumberWithSeparators(value: string): string {
    return value
      .replace(',', '.')
      .replace(/[.](?=.*[.])/g, '')
      .replace(/\s/g, '');
  }
}
